import { AudioRecorder } from 'components/audio_recorder/audio_recorder'
import { FormattingToolbar } from 'components/topics/composer/toolbars/formatting_toolbar'
import { RecordingToolbar } from 'components/topics/composer/toolbars/recording_toolbar'
import { ToolbarButton } from 'components/topics/composer/toolbars/toolbar_button'
import ErrorBoundary from 'containers/error_boundary'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { css } from 'design'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import {
  ComposerCameraIcon,
  ComposerPhotoIcon,
  ComposerToolbarToggleIcon,
  MicrophoneOn,
  VideoOnIcon,
} from '../../../images'
import {
  ComposerHeadingsIcon,
  ComposerNoteIcon,
  ComposerSubmitIcon,
} from '../../../images'
import {
  AppLocationContext,
  WIDGET_VIEW,
} from '../../../pages/admin/app_location_provider'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { ResponsiveTrixFormProps } from '../trix_form'

export const MobileTrixForm = React.forwardRef<
  HTMLFormElement,
  ResponsiveTrixFormProps
>((props, ref) => {
  const {
    id,
    isEditing,
    passUpFocusAtStart,
    onActiveTypeChanged,
    activeType = 'message',
    focusAtStart,
    handleSubmit,
    isRecording,
    isSubmitDisabled,
    children,
    editor,
    setIsRecording,
    hasAudioDevices,
    hasVideoDevices,
    handleKeyDown,
  } = props
  const [isToolbarExpanded, setIsToolbarExpanded] = useState<boolean>(false)
  const [isFormatting, setIsFormatting] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setAppLocation } = useContext(AppLocationContext)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  useEffect(() => {
    if (focusAtStart && passUpFocusAtStart) {
      passUpFocusAtStart(focusAtStart)
    }
  }, [focusAtStart, passUpFocusAtStart])

  useEffect(() => {
    if (!fileInputRef.current) {
      return
    }

    const fileInput = fileInputRef.current

    const insertAttachment = (file: File) => {
      if (!editor) {
        ErrorBoundary.logErrorToMyService(
          new Error('Trix editor is not available for file attachment'),
        )
        return
      }

      editor.insertFile(file)
    }

    const onFileInputChanged = () => {
      const { files } = fileInput
      if (files) {
        Array.from(files).forEach(insertAttachment)
      }

      // Clear the value from the file input, this prevents the ghost file behaviour
      // The change event would sometimes not fire if the same file was selected multiple times
      fileInput.value = ''
    }

    fileInput.addEventListener('change', onFileInputChanged)

    return () => {
      fileInput.removeEventListener('change', onFileInputChanged)
    }
  }, [editor])

  const trixAddAttachment = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!fileInputRef.current) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      fileInputRef.current?.click()
    },
    [],
  )

  const navToVideoRecorder = () => {
    setAppLocation({
      widgetView: WIDGET_VIEW.VIDEO_RECORDER,
    })
  }

  const navToAudioRecorder = () => {
    setAppLocation({
      widgetView: WIDGET_VIEW.AUDIO_RECORDER,
    })
  }

  return (
    <form
      key={`trix-form${id}`}
      ref={ref}
      onKeyDown={liveChatApp ? handleKeyDown : undefined}
      data-cy="composer-trix"
      className={composerFormStyles({
        variant:
          isToolbarExpanded || isFormatting || isRecording
            ? 'split'
            : 'default',
      })}
    >
      <Stack
        direction="row"
        gap="2"
        css={{
          width: '100%',
          height: '100%',
          alignItems: 'flex-end',

          padding: isEditing ? '0 $3 $2 0' : liveChatApp ? '$4 0' : '$4',
          '> button': {
            marginBottom: 5,
          },
        }}
      >
        <ToolbarButton
          onClick={() => {
            setIsToolbarExpanded((b) => !b)
            setIsFormatting(false)
            setIsRecording(false)
          }}
          toggled={isToolbarExpanded}
          className={toolbarToggleButtonStyles({
            toggled: isToolbarExpanded,
          })}
          title={isToolbarExpanded ? 'Collapse Toolbar' : 'Expand Toolbar'}
        >
          <ComposerToolbarToggleIcon />
        </ToolbarButton>

        {!isToolbarExpanded && isSubmitDisabled && (
          <ToolbarButton
            title="Attach Photos or Files"
            tabIndex={-1}
            activeType={activeType}
            onClick={trixAddAttachment}
          >
            <ComposerCameraIcon />
          </ToolbarButton>
        )}

        <Stack direction="row" gap="2" className={editorContainerStyles()}>
          {children}

          {!isSubmitDisabled && (
            <ToolbarButton
              className={cn(
                'composer-toolbar__submit',
                submitButtonStyles({ isEditing }),
              )}
              activeType={activeType}
              variant="primary"
              disabled={isSubmitDisabled}
              data-releasefocus="true"
              onClick={(e) => {
                // We don't want the keyboard to force submission of the form - just when this button is pressed
                handleSubmit && handleSubmit(e)

                setIsToolbarExpanded(false)
              }}
              title="Send Message"
              desktop={false}
            >
              <ComposerSubmitIcon
                width={15}
                height={15}
                viewBox={'5 5 15 15'}
              />
            </ToolbarButton>
          )}
        </Stack>
      </Stack>

      <trix-toolbar
        id="my_toolbar"
        data-testid="composer-toolbar__mobile"
        data-visibility={isToolbarExpanded ? 'visible' : 'hidden'}
      >
        <Stack
          gap="2"
          direction="row"
          css={{ width: '100%' }}
          className={trixToolbarStyles()}
        >
          <Stack
            gap="2"
            direction="row"
            className={toolbarContainerStyles({ isFormatting })}
          >
            {setIsFormatting && (
              <ToolbarButton
                onClick={() => {
                  setIsFormatting((f: boolean) => !f)
                  if (setIsRecording) {
                    setIsRecording(false)
                  }
                }}
                toggled={isFormatting}
                activeType={activeType}
                title={isFormatting ? 'Stop formatting' : 'Apply formatting'}
                desktop={false}
              >
                <ComposerHeadingsIcon />
              </ToolbarButton>
            )}

            {!isFormatting && (
              <>
                <ToolbarButton
                  id="composer-toolbar__attach-action"
                  data-trix-action="attachFiles"
                  title="Attach Photo"
                  tabIndex={-1}
                  activeType={activeType}
                  desktop={false}
                >
                  <ComposerCameraIcon />
                </ToolbarButton>

                <ToolbarButton
                  data-trix-action="attachFiles"
                  title="Attach Files"
                  tabIndex={-1}
                  activeType={activeType}
                  desktop={false}
                >
                  <ComposerPhotoIcon />
                </ToolbarButton>

                {!liveChatApp && onActiveTypeChanged && (
                  <ToolbarButton
                    onClick={onActiveTypeChanged}
                    toggled={activeType === 'note'}
                    disabled={!onActiveTypeChanged}
                    className="composer-toolbar__notetoggle"
                    activeType={activeType}
                    title={
                      activeType === 'message'
                        ? 'Write a Note'
                        : 'Write a Message'
                    }
                    desktop={false}
                  >
                    <ComposerNoteIcon />
                  </ToolbarButton>
                )}

                {liveChatApp && hasVideoDevices && hasAudioDevices && (
                  <ToolbarButton onClick={navToVideoRecorder} desktop={false}>
                    <VideoOnIcon />
                  </ToolbarButton>
                )}

                {hasAudioDevices && liveChatApp ? (
                  <ToolbarButton onClick={navToAudioRecorder} desktop={false}>
                    <MicrophoneOn />
                  </ToolbarButton>
                ) : (
                  <AudioRecorder
                    editor={editor}
                    isRecording={isRecording}
                    activeType={activeType}
                    setIsRecording={() => {
                      setIsRecording((f: boolean) => !f)
                      if (setIsFormatting) {
                        setIsFormatting(false)
                      }
                    }}
                    desktop={false}
                  />
                )}
              </>
            )}
          </Stack>

          {isFormatting && (
            <FormattingToolbar desktop={false} activeType={activeType} />
          )}
          {isRecording && <RecordingToolbar />}
        </Stack>
      </trix-toolbar>

      <input
        type="file"
        id="trix-editor__fileinput__hidden"
        className={hiddenFileInputStyles()}
        multiple
        ref={fileInputRef}
      />
    </form>
  )
})

MobileTrixForm.displayName = 'MobileTrixForm'

const composerFormStyles = css({
  display: 'flex',
  width: '100%',
  height: '100%',
  maxHeight: '50vh',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'relative',

  '#trix-editor-top-level': {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '$2 $3',
    minHeight: 'unset',
    height: '100%',
    paddingRight: '58px',
  },

  variants: {
    variant: {
      default: {
        'trix-toolbar': {
          borderTop: '1px solid transparent',
          display: 'none',
        },
      },
      split: {
        flexDirection: 'column',
        'trix-toolbar': {
          borderTop: '1px solid $grey200',
          width: '100%',
          justifyContent: 'flex-start',
          height: 'unset',
          paddingTop: '$3',
          paddingBottom: '$3',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const trixToolbarStyles = css({
  display: 'flex',
  flex: 'none',
  alignItems: 'flex-start',
  padding: '0 $4 $1',
  mixBlendMode: 'multiply',

  '.topic__theme--dark &': {
    mixBlendMode: 'normal',
  },
})

const editorContainerStyles = css({
  flex: '1',
  border: '1px solid $grey200',
  borderRadius: '$3',
  alignItems: 'center',
  overflowY: 'auto',
  position: 'relative',
  height: '100%',
})

const submitButtonStyles = css({
  margin: '5px 5px 5px 0',
  padding: '7px $4',
  minHeight: 'unset',
  position: 'absolute',
  bottom: 0,
  right: 0,
  zIndex: 1,
})

const toolbarToggleButtonStyles = css({
  'path:last-child': {
    transition: 'transform 240ms ease-out',
    transform: 'rotate(0)',
    transformOrigin: '50% 50%',
  },
  variants: {
    toggled: {
      true: {
        'path:last-child': {
          transform: 'rotate(45deg)',
        },
      },
    },
  },
})

const toolbarContainerStyles = css({
  variants: {
    isFormatting: {
      true: {
        width: 'auto',
      },
      false: {
        width: '100%',
      },
    },
  },
})

const hiddenFileInputStyles = css({
  '&:not(:focus):not(:active)': {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
})
